import React, { useState, useEffect } from 'react';
import { FaEdit, FaTrash, FaPlus, FaEye, FaEyeSlash, FaInfoCircle } from 'react-icons/fa';
import pb from '../lib/pocketbase';
import { useCompany } from '../context/CompanyContext';

const Authorized = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [formData, setFormData] = useState({
    Name: '',
    Surname: '',
    AccessRight: '',
    Agreement: true
  });
  const [newUserData, setNewUserData] = useState({
    Name: '',
    Surname: '',
    Password: '',
    PasswordConfirm: '',
    email: '',
    emailVisibility: true,
    CanAccessPortal: true,
    AccessRight: 'Operator'
  });
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [isMounted, setIsMounted] = useState(false);
  const { currentCompanyId } = useCompany();

  // Toast component
  const Toast = ({ message, type }) => {
    if (!message) return null;
    
    return (
      <div className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg text-white z-50 transform transition-all duration-300 ${
        type === 'success' ? 'bg-[#A1E744]' : 'bg-red-500'
      }`}>
        {message}
      </div>
    );
  };

  // Function to show toast
  const showToast = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  useEffect(() => {
    if (isMounted) {
      fetchAuthorizedUsers();
    }
  }, [isMounted, currentCompanyId]);

  const fetchAuthorizedUsers = async () => {
    if (!isMounted) return; // Prevent API call if component is unmounting

    try {
      setLoading(true);
      const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
      const companyId = currentCompanyId || pbAuth?.record?.CompanyID;

      if (!companyId) {
        showToast('Company ID not found', 'error');
        return;
      }
      console.log("companyId>>> in authorized.js", companyId);
      // Fetch all contacts for the company with CanAccessPortal = true and isDeleted = false
      const records = await pb.collection('Contacts').getList(1, 50, {
        filter: `CompanyID.id = "${companyId}" && CanAccessPortal = true && isDeleted = false`
      });

      if (isMounted) { // Only update state if component is still mounted
        if (records && records.items) {
          setUsers(records.items);
        } else {
          setUsers([]);
        }
      }
    } catch (error) {
      if (isMounted) { // Only update state if component is still mounted
        console.error('Error fetching authorized users:', error);
        showToast('Failed to fetch authorized users', 'error');
        setUsers([]);
      }
    } finally {
      if (isMounted) { // Only update state if component is still mounted
        setLoading(false);
      }
    }
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      Name: user.Name,
      Surname: user.Surname,
      AccessRight: user.AccessRight,
      Agreement: true
    });
    setEditModalOpen(true);
  };

  const handleDelete = async (user) => {
    try {
      await pb.collection('Contacts').update(user.id, { isDeleted: true });
      
      // Update local state
      setUsers(users.filter(u => u.id !== user.id));
      showToast('User deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting user:', error);
      showToast('Failed to delete user', 'error');
    }
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;

    try {
      await pb.collection('Contacts').update(selectedUser.id, formData);
      
      // Update local state
      setUsers(users.map(user => 
        user.id === selectedUser.id ? { ...user, ...formData } : user
      ));
      
      setEditModalOpen(false);
      showToast('User updated successfully', 'success');
    } catch (error) {
      console.error('Error updating user:', error);
      showToast('Failed to update user', 'error');
    }
  };

  const handleAddUser = async () => {
    try {
      const pbAuth = JSON.parse(localStorage.getItem('pocketbase_auth') || '{}');
      const companyId = currentCompanyId || pbAuth?.record?.CompanyID;

      // Create payload with correct field names
      const payload = {
        Name: newUserData.Name,
        Surname: newUserData.Surname,
        password: newUserData.Password,
        passwordConfirm: newUserData.PasswordConfirm,
        email: newUserData.email,
        emailVisibility: newUserData.emailVisibility,
        CanAccessPortal: newUserData.CanAccessPortal,
        AccessRight: newUserData.AccessRight,
        CompanyID: companyId,
        isDeleted: false
      };

      const newUser = await pb.collection('Contacts').create(payload);
      setUsers([...users, newUser]);
      setAddModalOpen(false);
      setNewUserData({
        Name: '',
        Surname: '',
        Password: '',
        PasswordConfirm: '',
        email: '',
        emailVisibility: true,
        CanAccessPortal: true,
        AccessRight: 'Operator'
      });
      showToast('User added successfully', 'success');
    } catch (error) {
      console.error('Error adding user:', error);
      showToast('Failed to add user', 'error');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-gray-200 rounded w-1/4"></div>
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-4xl font-bold text-[#0a1744] bg-gradient-to-r from-[#0a1744] to-[#A1E744] bg-clip-text text-transparent mb-8">Authorized Users</h1>
      <div className="bg-white rounded-lg shadow-lg border-2 border-gray-100 p-6">
        <div className="flex justify-between items-center mb-8">
          <button
            onClick={() => setAddModalOpen(true)}
            className="px-4 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors duration-200 flex items-center"
          >
            <FaPlus className="mr-2" />
            Add User
          </button>
        </div>

        {/* Toast Notification */}
        {toast.show && <Toast message={toast.message} type={toast.type} />}

        {/* Users Table */}
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access Level</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agreement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{user.Name} {user.Surname}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{user.AccessRight}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={true}
                        readOnly
                        className="h-4 w-4 text-[#A1E744] focus:ring-[#A1E744] border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-[#A1E744] hover:text-[#0a1744] mr-4"
                      >
                        <FaEdit className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(user)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <FaTrash className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 text-[#0a1744]">Edit User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={formData.Name}
                  onChange={(e) => setFormData({ ...formData, Name: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Surname</label>
                <input
                  type="text"
                  value={formData.Surname}
                  onChange={(e) => setFormData({ ...formData, Surname: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Access Level</label>
                <select
                  value={formData.AccessRight}
                  onChange={(e) => setFormData({ ...formData, AccessRight: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                >
                  <option value="SuperUser">SuperUser</option>
                  <option value="Administrator">Administrator</option>
                  <option value="Accounting">Accounting</option>
                  <option value="Sales">Sales</option>
                  <option value="Technical">Technical</option>
                  <option value="Logistic">Logistic</option>
                  <option value="Operator">Operator</option>
                  <option value="Just Support">Just Support</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.Agreement}
                  onChange={(e) => setFormData({ ...formData, Agreement: e.target.checked })}
                  className="h-4 w-4 text-[#A1E744] focus:ring-[#A1E744] border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Agreement</label>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors duration-200"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {addModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 text-[#0a1744]">Add New User</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <input
                  type="text"
                  value={newUserData.Name}
                  onChange={(e) => setNewUserData({ ...newUserData, Name: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Surname</label>
                <input
                  type="text"
                  value={newUserData.Surname}
                  onChange={(e) => setNewUserData({ ...newUserData, Surname: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Email</label>
                <input
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newUserData.emailVisibility}
                  onChange={(e) => setNewUserData({ ...newUserData, emailVisibility: e.target.checked })}
                  className="h-4 w-4 text-[#A1E744] focus:ring-[#A1E744] border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Email Visibility</label>
                <div className="relative group ml-2">
                  <FaInfoCircle className="text-gray-400 hover:text-gray-600 cursor-help" />
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-50">
                    This field determines if the user can log in with Email and is shown publically
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newUserData.Password}
                    onChange={(e) => setNewUserData({ ...newUserData, Password: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Password Confirm</label>
                <div className="relative">
                  <input
                    type={showPasswordConfirm ? "text" : "password"}
                    value={newUserData.PasswordConfirm}
                    onChange={(e) => setNewUserData({ ...newUserData, PasswordConfirm: e.target.value })}
                    className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  >
                    {showPasswordConfirm ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Access Level</label>
                <select
                  value={newUserData.AccessRight}
                  onChange={(e) => setNewUserData({ ...newUserData, AccessRight: e.target.value })}
                  className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#A1E744] focus:border-[#A1E744]"
                >
                  <option value="SuperUser">SuperUser</option>
                  <option value="Administrator">Administrator</option>
                  <option value="Accounting">Accounting</option>
                  <option value="Sales">Sales</option>
                  <option value="Technical">Technical</option>
                  <option value="Logistic">Logistic</option>
                  <option value="Operator">Operator</option>
                  <option value="Just Support">Just Support</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={newUserData.CanAccessPortal}
                  onChange={(e) => setNewUserData({ ...newUserData, CanAccessPortal: e.target.checked })}
                  className="h-4 w-4 text-[#A1E744] focus:ring-[#A1E744] border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">Can Access Portal</label>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-4">
              <button
                onClick={() => {
                  setAddModalOpen(false);
                  setShowPassword(false);
                  setShowPasswordConfirm(false);
                }}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleAddUser}
                className="px-4 py-2 bg-[#0a1744] text-white rounded-lg hover:bg-[#A1E744] hover:text-[#0a1744] transition-colors duration-200"
              >
                Add User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Authorized; 